<template>
  <div class="login-page">
    <!-- Left side - Form -->
    <div class="form-section">
      <div class="form-container">
        <div class="logo"></div>

        <div class="form-content">
          <h2>Welcome back</h2>
          <p class="subtitle">
            Welcome to the NetNest Platform Management system. Sign in to your
            NetNest account
          </p>

          <!-- Error message -->
          <div v-if="error" class="error-message">
            <div class="error-icon">⚠️</div>
            <p>{{ error }}</p>
          </div>

          <form @submit.prevent="handleLogin" class="login-form">
            <div class="form-group">
              <label for="identifier">Username or Email</label>
              <input
                type="text"
                id="identifier"
                v-model="identifier"
                required
                placeholder="username or email@example.com"
                :disabled="loading"
              />
            </div>

            <div class="form-group">
              <label for="password">Password</label>
              <div class="password-input-container">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="password"
                  required
                  placeholder="••••••••"
                  :disabled="loading"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                  :disabled="loading"
                >
                  {{ showPassword ? "🙈" : "👁️" }}
                </button>
              </div>
            </div>

            <div class="form-options">
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  v-model="rememberMe"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
                Remember me
              </label>
              <router-link to="/forgot-password" class="forgot-password">
                Forgot password?
              </router-link>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="large"
              :disabled="loading || !identifier || !password"
              :loading="loading"
              loading-text="Signing in..."
              block
              :active="true"
              class="login-btn"
            >
              Sign In
            </Button>
          </form>

          <div class="divider">
            <span>OR</span>
          </div>

          <div class="social-login">
            <button
              class="social-btn google"
              @click="handleGoogleSignIn"
              :disabled="loading || googleLoading"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285f4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34a853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#fbbc05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#ea4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span v-if="!googleLoading">Continue with Google</span>
              <span v-else>Signing in with Google...</span>
            </button>
            <!-- Apple sign-in removed per policy -->
          </div>
        </div>
      </div>
    </div>

    <!-- Right side - Visual -->
    <div class="visual-section"></div>
  </div>
</template>

<script>
import Button from "../components/Button.vue";
import AuthService from "../services/auth.js";
import ApiService from "../services/api.js";

export default {
  name: "Login",
  components: {
    Button,
  },
  data() {
    return {
      identifier: "",
      password: "",
      rememberMe: false,
      loading: false,
      error: null,
      showTestUsers: false,
      testUsers: [],
      showPassword: false,
      googleLoading: false,
    };
  },
  methods: {
    async handleLogin() {
      this.loading = true;
      this.error = null;

      try {
        // Prepare credentials
        const credentials = {
          password: this.password,
        };

        // Add username or email based on input
        if (this.identifier.includes("@")) {
          credentials.email = this.identifier;
        } else {
          credentials.username = this.identifier;
        }

        // Attempt login
        const result = await AuthService.login(credentials);

        if (result.success) {
          // Show success and redirect
          this.$router.push("/");
        } else {
          this.error = result.error || "Login failed";
          console.error("Login failed:", result.error);
        }
      } catch (error) {
        console.error("Login error:", error);
        this.error = "An error occurred during login";
      } finally {
        this.loading = false;
      }
    },

    async handleGoogleSignIn() {
      // Trigger Google Identity Services prompt and handle credential via callback
      this.googleLoading = true;
      this.error = null;

      try {
        // If running inside Electron, use native PKCE loopback flow implemented in main
        if (window && window.electronAPI && window.electronAPI.googleSignIn) {
          const result = await window.electronAPI.googleSignIn();
          if (!result || !result.success) {
            this.error =
              (result && result.error) || "Google aanmelden mislukt (desktop)";
            return;
          }

          const backendJson = result.data;
          if (backendJson && backendJson.success) {
            const token = backendJson.data.token;
            const user = backendJson.data.user;

            // Apply auth state locally (mirror what AuthService.loginWithGoogle does)
            AuthService.token = token;
            AuthService.user = user;
            AuthService.isAuthenticated = true;
            localStorage.setItem("authToken", token);
            localStorage.setItem("user", JSON.stringify(user));

            // Save userId for agenda API calls
            const userId = user._id || user.id;
            if (userId) {
              localStorage.setItem("userId", userId);
            }

            ApiService.setAuthToken(token);

            this.$router.push("/");
            return;
          } else {
            this.error =
              (backendJson && backendJson.error) ||
              "Google login mislukt (backend)";
            return;
          }
        }

        if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
          this.error =
            "Geen Google Client ID geconfigureerd (VITE_GOOGLE_CLIENT_ID)";
          return;
        }

        await this.loadGoogleClient();

        // Initialize Google Identity Services with a callback
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: this.handleGoogleCredential,
        });

        // Prompt the One Tap / chooser. The callback will be invoked with a credential.
        window.google.accounts.id.prompt();
      } catch (err) {
        console.error("Google sign-in error", err);
        this.error = "Google aanmelden is mislukt";
      } finally {
        this.googleLoading = false;
      }
    },

    async handleGoogleCredential(response) {
      // response.credential contains the ID token
      if (!response || !response.credential) {
        this.error = "Geen Google credential ontvangen";
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const result = await AuthService.loginWithGoogle(response.credential);

        if (result.success) {
          this.$router.push("/");
        } else {
          this.error = result.error || "Google login mislukt";
        }
      } catch (error) {
        console.error("Google login error:", error);
        this.error = "Er is een fout opgetreden tijdens Google login";
      } finally {
        this.loading = false;
      }
    },

    loadGoogleClient() {
      return new Promise((resolve, reject) => {
        if (
          window.google &&
          window.google.accounts &&
          window.google.accounts.id
        ) {
          return resolve();
        }

        const existing = document.querySelector("script[data-google-identity]");
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", (e) => reject(e));
          return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.setAttribute("data-google-identity", "1");
        script.onload = () => resolve();
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      });
    },
  },

  // Check if already logged in
  async mounted() {
    // Set page title as requested
    try {
      document.title = "Log in | NetNest Platform Management System";
    } catch (e) {
      // ignore in non-browser environments
    }

    if (AuthService.isAuthenticated) {
      this.$router.push("/");
    }
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  height: 100vh;
  background: #f8fafc;
  overflow: hidden;
}

/* Left side - Form Section */
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
  position: relative;
  overflow-y: auto;
  max-height: 100vh;
}

.form-container {
  width: 100%;
  max-width: 400px;
}

.logo {
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.5rem;
  background-image: url("../assets/images/Logo.svg");
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Right side - Visual */
.visual-section {
  background-image: url("/assets/images/login-visual.jpg");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  flex: 1;
  height: 100vh;
  max-height: 100vh;
  display: none;
}

.form-content h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.subtitle {
  color: #64748b;
  margin-bottom: 2rem;
  font-size: 1rem;
  line-height: 1.5;
}

/* Test users info */
.test-users-info {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.test-users-info h4 {
  margin-bottom: 0.5rem;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 600;
}

.test-user {
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  padding: 0.25rem 0;
  cursor: pointer;
  transition: color 0.2s;
  border-radius: 4px;
  padding-left: 0.5rem;
}

.test-user:hover {
  color: #6366f1;
  background: rgba(99, 102, 241, 0.05);
}

.test-user strong {
  color: #6366f1;
  font-weight: 600;
}

.test-user small {
  color: #94a3b8;
  margin-left: 0.5rem;
}

.show-test-btn,
.hide-test-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  cursor: pointer;
  margin-bottom: 1.5rem;
  width: 100%;
  transition: all 0.2s;
  font-weight: 500;
}

.show-test-btn:hover,
.hide-test-btn:hover {
  background: #e2e8f0;
}

/* Error message */
.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-icon {
  font-size: 1.2rem;
}

.error-message p {
  margin: 0;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Form styling */
.login-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary_color);
  box-shadow: 0 0 0 3px var(--primary_color_alpha_10);
}

.form-group input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.password-input-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: #9ca3af;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: all 0.2s;
}

.password-toggle:hover {
  background-color: #f3f4f6;
  color: #6b7280;
}

.password-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  color: #6b7280;
}

.checkbox-container input {
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
  accent-color: var(--primary_color);
}

.forgot-password {
  color: var(--primary_color);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s;
}

.forgot-password:hover {
  color: var(--primary_color);
  opacity: 0.8;
}

/* Custom button styling for login */
.login-btn {
  background: var(--primary_color) !important;
  color: var(--background) !important;
  border: 2px solid var(--primary_color) !important;
  box-shadow: 0 4px 15px var(--primary_color_alpha_25) !important;
  margin-bottom: 1.5rem;
}

.login-btn:hover:not(:disabled) {
  background: var(--primary_color) !important;
  border-color: var(--primary_color) !important;
  transform: translateY(-1px);
  opacity: 0.9;
}

.login-btn:disabled {
  background: var(--grey) !important;
  border-color: var(--grey) !important;
  box-shadow: none !important;
  opacity: 0.6;
}

/* Divider */
.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}

.divider span {
  background: white;
  color: #9ca3af;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Social login */
.social-login {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.social-btn:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

/* Apple button styling removed when Apple sign-in was disabled */

/* Signup link */
.signup-link {
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
}

.signup-link a {
  color: var(--primary_color);
  text-decoration: none;
  font-weight: 500;
}

.signup-link a:hover {
  color: var(--primary_color);
  opacity: 0.8;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

.content-overlay {
  padding: 3rem;
  color: white;
  max-width: 480px;
  text-align: left;
}

.content-overlay h3 {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.content-overlay p {
  font-size: 1.1rem;
  line-height: 1.6;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Show visual section only on desktop */
@media (min-width: 1024px) {
  .visual-section {
    display: block;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-page {
    flex-direction: column;
  }

  .content-overlay {
    padding: 2rem;
    text-align: center;
  }

  .content-overlay h3 {
    font-size: 1.8rem;
  }

  .content-overlay p {
    font-size: 1rem;
  }

  .form-section {
    padding: 1.5rem;
  }

  .form-options {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .forgot-password {
    align-self: center;
  }
}

@media (max-width: 480px) {
  .form-section {
    padding: 1rem;
  }

  .form-content h2 {
    font-size: 1.75rem;
  }

  .visual-section {
    min-height: 150px;
  }

  .content-overlay h3 {
    font-size: 1.5rem;
  }
}
</style>
